import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Command,
  CommandInput,
  CommandList,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { BookOpen, ArrowRight, Sparkles, SearchX } from "lucide-react";
import api from "@/lib/api";

/**
 * Phase B3.1 — Global Cmd-K (Ctrl-K on Windows/Linux) KB search palette.
 *
 * Mounted in `Header.jsx` for every authenticated page. The trigger
 * (search box on desktop, icon-only on mobile) lives next to the
 * `<HelpDropdown />`. Both surface and the keyboard shortcut open the
 * same modal.
 *
 * Server-side search via `GET /api/kb/articles?q=` (Mongo $text index).
 * We disable cmdk's client-side fuzzy filtering (`shouldFilter={false}`)
 * so the visible order matches the backend ranking.
 *
 * Accessibility: shadcn `Dialog` provides focus-trap + `role="dialog"`
 * + Esc-to-close out of the box.
 */
export default function KbCommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);
  const debounceRef = useRef(null);

  // Recent articles — fetched once on first open. Cheap (limit=5).
  useEffect(() => {
    if (!open || recent.length > 0) return;
    api.get("/kb/articles", { params: { limit: 5 } })
      .then(({ data }) => setRecent(data.items || []))
      .catch(() => { /* silently ignore — palette stays useful even on failure */ });
  }, [open, recent.length]);

  // Reset query when the palette closes.
  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
    }
  }, [open]);

  // Debounced server-side search (200ms; min 2 chars).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/kb/articles", { params: { q, limit: 8 } });
        setResults(data.items || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  const goToArticle = useCallback((slug) => {
    onOpenChange(false);
    navigate(`/help/kb/${slug}`);
  }, [navigate, onOpenChange]);

  const goToAllResults = useCallback(() => {
    onOpenChange(false);
    navigate(`/help/kb/search?q=${encodeURIComponent(query.trim())}`);
  }, [navigate, onOpenChange, query]);

  const trimmed = query.trim();
  const hasQuery = trimmed.length >= 2;
  const showNoResults = hasQuery && !loading && results.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 max-w-2xl" aria-labelledby="kb-cmd-k-title">
        <span id="kb-cmd-k-title" className="sr-only">Search the knowledge base</span>
        {/* `shouldFilter={false}` — results come pre-ranked from Mongo's
            $text index; we don't want cmdk's client-side fuzzy filter on
            top, which would hide rows whose `value` prop doesn't substring-
            match the query. */}
        <Command
          shouldFilter={false}
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
        >
          <CommandInput
            placeholder="Search the knowledge base…"
            value={query}
            onValueChange={setQuery}
            data-testid="kb-cmd-k-input"
            autoFocus
          />
          <CommandList data-testid="kb-cmd-k-list">
            {!hasQuery && (
              <CommandGroup heading="Recent articles">
                {recent.length === 0 ? (
                  <div className="px-3 py-6 text-center text-xs text-navy-400">
                    Type to search articles, or press Esc to close.
                  </div>
                ) : (
                  recent.map((a) => (
                    <CommandItem
                      key={a.id}
                      value={`recent-${a.id}`}
                      onSelect={() => goToArticle(a.slug)}
                      data-testid={`kb-cmd-k-recent-${a.slug}`}
                    >
                      <BookOpen className="text-orange-500" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-navy-900 dark:text-white truncate">{a.title}</div>
                        {a.summary && (
                          <div className="text-xs text-navy-500 dark:text-navy-400 truncate">{a.summary}</div>
                        )}
                      </div>
                      <Sparkles className="w-3.5 h-3.5 text-navy-300 ml-2" />
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
            )}

            {hasQuery && loading && (
              <div className="px-3 py-6 text-center text-xs text-navy-400" data-testid="kb-cmd-k-loading">
                Searching…
              </div>
            )}

            {hasQuery && !loading && results.length > 0 && (
              <CommandGroup heading="Articles">
                {results.map((a) => (
                  <CommandItem
                    key={a.id}
                    value={`result-${a.id}`}
                    onSelect={() => goToArticle(a.slug)}
                    data-testid={`kb-cmd-k-result-${a.slug}`}
                  >
                    <BookOpen className="text-orange-500" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-navy-900 dark:text-white truncate">{a.title}</span>
                        {a.category && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-navy-100 dark:bg-navy-700 text-navy-700 dark:text-navy-200 shrink-0">
                            {a.category}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-navy-500 dark:text-navy-400 truncate">
                        {a.snippet || a.summary || ""}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {showNoResults && (
              <div className="px-3 py-8 text-center" data-testid="kb-cmd-k-empty">
                <SearchX className="w-6 h-6 text-navy-300 mx-auto mb-2" />
                <div className="text-sm text-navy-700 dark:text-navy-100">
                  No articles match <strong>"{trimmed}"</strong>.
                </div>
                <div className="text-xs text-navy-500 mt-1">
                  Try different keywords or{" "}
                  <button
                    type="button"
                    onClick={() => { onOpenChange(false); navigate("/help/kb"); }}
                    className="text-orange-600 underline hover:text-orange-700"
                    data-testid="kb-cmd-k-browse-link"
                  >
                    browse the knowledge base
                  </button>.
                </div>
              </div>
            )}

            {hasQuery && !loading && results.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    value="see-all-results"
                    onSelect={goToAllResults}
                    data-testid="kb-cmd-k-see-all"
                    className="text-orange-600 dark:text-orange-400"
                  >
                    <ArrowRight />
                    <span className="font-medium">See all results for "{trimmed}"</span>
                    <CommandShortcut>Enter</CommandShortcut>
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
